import { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';

import { createLeadSchema, type CreateLeadInput } from '@realvisor/schemas';
import { Button, Input, Modal } from '@realvisor/ui';
import { useCreateLead, useProjects, type LeadTypeEnum } from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';
import { api } from '@/lib/api';

// URL widgetu - v produkci bude jiná
const WIDGET_BASE_URL = import.meta.env['VITE_WIDGET_URL'] || 'http://localhost:5175';
const MAPY_API_KEY = import.meta.env['VITE_MAPY_API_KEY'] || '';

// Typy leadů s ikonami a barvami
const LEAD_TYPES: { 
  value: LeadTypeEnum; 
  label: string; 
  description: string; 
  color: string; 
  formType?: 'valuo' | 'hypokalkulacka' | 'prescoring';
  kind?: 'sale' | 'lease';
}[] = [
  { value: 'sale', label: 'Prodej', description: 'Klient chce prodat nemovitost', color: 'bg-green-100 text-green-700 border-green-200', formType: 'valuo', kind: 'sale' },
  { value: 'purchase', label: 'Koupě', description: 'Klient hledá nemovitost ke koupi', color: 'bg-blue-100 text-blue-700 border-blue-200', formType: 'valuo', kind: 'sale' },
  { value: 'rent', label: 'Pronájem', description: 'Klient hledá pronájem', color: 'bg-purple-100 text-purple-700 border-purple-200', formType: 'valuo', kind: 'lease' },
  { value: 'hypo', label: 'Hypotéka', description: 'Hypoteční kalkulačka + prescoring', color: 'bg-amber-100 text-amber-700 border-amber-200', formType: 'hypokalkulacka' },
  { value: 'invest', label: 'Investice', description: 'Investiční záměr', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  { value: 'pm', label: 'Správa', description: 'Správa nemovitosti', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'other', label: 'Ostatní', description: 'Jiný typ leadu', color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

interface CreateLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (leadId: string) => void;
}

export default function CreateLeadModal({ isOpen, onClose, onSuccess }: CreateLeadModalProps) {
  const { currentProject, isAllProjects } = useProject();
  const { data: projects } = useProjects();
  const createLead = useCreateLead();

  const [step, setStep] = useState<'type' | 'project' | 'form' | 'simple'>('type');
  const [selectedLeadType, setSelectedLeadType] = useState<LeadTypeEnum | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  // react-hook-form pro jednoduchý formulář
  const {
    register: registerField,
    handleSubmit: handleFormSubmit,
    formState: { errors: formErrors },
    reset: resetForm,
    setValue,
  } = useForm<CreateLeadInput>({
    resolver: zodResolver(createLeadSchema),
  });

  // Podepsaný token pro ruční založení ocenění (API bez něj kód crm-manual odmítne).
  const [crmToken, setCrmToken] = useState('');
  useEffect(() => {
    setCrmToken('');
    if (!selectedProjectId) return;
    let zruseno = false;
    api.get<{ token: string }>(`/public/valuo/crm-token/${selectedProjectId}`)
      .then(r => { if (!zruseno) setCrmToken(r.token); })
      .catch(() => { if (!zruseno) toast.error('Formulář se nepodařilo připravit, zkuste to znovu'); });
    return () => { zruseno = true; };
  }, [selectedProjectId]);

  // Nastavit projectId když se změní currentProject
  useEffect(() => {
    if (currentProject?.id) {
      setSelectedProjectId(currentProject.id);
    }
  }, [currentProject?.id]);

  // Poslouchání zpráv z iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Ověřit, že zpráva pochází z widgetu
      if (!event.origin.includes('vercel.app') && !event.origin.includes('localhost')) {
        return;
      }
      
      if (event.data?.type === 'REALVISOR_FORM_SUCCESS') {
        toast.success('Lead vytvořen');
        // Data přišla z formuláře, lead byl vytvořen
        // API vrací { success: true, leadId: "xxx", message: "..." }
        const leadId = event.data?.data?.leadId || event.data?.leadId;
        if (leadId) {
          onSuccess?.(leadId);
        }
        handleClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSuccess]);

  const selectedType = LEAD_TYPES.find(t => t.value === selectedLeadType);

  const handleSelectType = (type: LeadTypeEnum) => {
    setSelectedLeadType(type);
    setValue('lead_type', type);
    const projectId = selectedProjectId || currentProject?.id || '';
    if (projectId) {
      setValue('project_id', projectId);
    }
    const typeConfig = LEAD_TYPES.find(t => t.value === type);

    // Pokud je vybraný "Všechny projekty", musíme nejdřív vybrat projekt
    if (isAllProjects && !selectedProjectId) {
      setStep('project');
    } else if (typeConfig?.formType) {
      // Typ s formulářem (valuo/hypoteka)
      setStep('form');
    } else {
      // Jednoduchý typ (invest, pm, other)
      setStep('simple');
    }
  };

  const handleProjectSelect = () => {
    if (!selectedProjectId) {
      toast.error('Vyberte projekt');
      return;
    }
    setValue('project_id', selectedProjectId);

    const typeConfig = LEAD_TYPES.find(t => t.value === selectedLeadType);
    if (typeConfig?.formType) {
      setStep('form');
    } else {
      setStep('simple');
    }
  };

  const handleSimpleSubmit = async (data: CreateLeadInput) => {
    try {
      const result = await createLead.mutateAsync({
        project_id: data.project_id,
        first_name: data.first_name,
        last_name: data.last_name || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        lead_type: (data.lead_type || selectedLeadType || 'other') as LeadTypeEnum,
        expected_value: data.expected_value,
        notes: data.notes || undefined,
      });

      toast.success('Lead vytvořen');
      onSuccess?.(result.id);
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Nepodařilo se vytvořit lead');
    }
  };

  const handleClose = useCallback(() => {
    setStep('type');
    setSelectedLeadType(null);
    setSelectedProjectId(currentProject?.id || '');
    resetForm();
    onClose();
  }, [currentProject?.id, onClose, resetForm]);

  const handleBack = () => {
    if (step === 'project') {
      setStep('type');
    } else if (step === 'form' || step === 'simple') {
      if (isAllProjects) {
        setStep('project');
      } else {
        setStep('type');
      }
    }
  };

  // Sestavit URL pro iframe
  const getIframeUrl = () => {
    if (!selectedType?.formType || !selectedProjectId || !crmToken) return '';
    
    const params = new URLSearchParams({
      type: selectedType.formType,
      code: 'crm-manual', // Speciální kód pro manuální vytvoření
      kind: selectedType.kind || 'sale',
      projectId: selectedProjectId,
      crmToken,
      leadType: selectedLeadType || 'other',
    });
    
    // Přidat mapyApiKey pokud je k dispozici
    if (MAPY_API_KEY) {
      params.set('mapyApiKey', MAPY_API_KEY);
    }
    
    return `${WIDGET_BASE_URL}/embed.html?${params.toString()}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={step === 'form' ? 'xl' : 'lg'}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {step !== 'type' && (
              <button 
                onClick={handleBack}
                className="p-1 rounded hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {step === 'type' && 'Nový lead'}
                {step === 'project' && 'Vyberte projekt'}
                {step === 'form' && `${selectedType?.label}`}
                {step === 'simple' && `Nový lead - ${selectedType?.label}`}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {step === 'type' && 'Vyberte typ leadu'}
                {step === 'project' && 'Ke kterému projektu patří tento lead?'}
                {step === 'form' && 'Vyplňte údaje o nemovitosti'}
                {step === 'simple' && 'Vyplňte základní údaje'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Step 1: Výběr typu */}
        {step === 'type' && (
          <div className="grid grid-cols-2 gap-3">
            {LEAD_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => handleSelectType(type.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${type.color}`}
              >
                <div className="font-semibold">{type.label}</div>
                <div className="text-xs mt-1 opacity-75">{type.description}</div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Výběr projektu (pokud je "Všechny projekty") */}
        {step === 'project' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Projekt
              </label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Vyberte projekt</option>
                {projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={handleClose}>
                Zrušit
              </Button>
              <Button onClick={handleProjectSelect}>
                Pokračovat
              </Button>
            </div>
          </div>
        )}

        {/* Step 3a: Iframe s formulářem (valuo/hypoteka) */}
        {step === 'form' && (
          <div className="space-y-4">
            {/* Badge typu */}
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${selectedType?.color}`}>
                {selectedType?.label}
              </span>
            </div>
            
            {/* Iframe */}
            <div className="border rounded-lg overflow-hidden bg-gray-50" style={{ height: '500px' }}>
              {getIframeUrl() ? (
                <iframe
                  src={getIframeUrl()}
                  className="w-full h-full border-0"
                  title="Formulář"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">Připravuji formulář…</div>
              )}
            </div>
          </div>
        )}

        {/* Step 3b: Jednoduchý formulář (invest, pm, other) */}
        {step === 'simple' && (
          <form onSubmit={handleFormSubmit(handleSimpleSubmit)} className="space-y-4">
            {/* Badge typu */}
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${selectedType?.color}`}>
                {selectedType?.label}
              </span>
            </div>

            {/* Jméno a příjmení */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Jméno *"
                placeholder="Jan"
                error={formErrors.first_name?.message}
                {...registerField('first_name')}
              />
              <Input
                label="Příjmení"
                placeholder="Novák"
                {...registerField('last_name')}
              />
            </div>

            {/* Email a telefon */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="E-mail"
                type="email"
                placeholder="jan@example.cz"
                error={formErrors.email?.message}
                {...registerField('email')}
              />
              <Input
                label="Telefon"
                placeholder="+420 123 456 789"
                error={formErrors.phone?.message}
                {...registerField('phone')}
              />
            </div>

            {/* Očekávaná hodnota */}
            <Input
              label="Očekávaná hodnota (Kč)"
              type="number"
              placeholder="5 000 000"
              error={formErrors.expected_value?.message}
              {...registerField('expected_value')}
            />

            {/* Poznámky */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Poznámky
              </label>
              <textarea
                placeholder="Další informace o leadu..."
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                {...registerField('notes')}
              />
            </div>

            {/* Tlačítka */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" type="button" onClick={handleClose}>
                Zrušit
              </Button>
              <Button type="submit" disabled={createLead.isPending}>
                {createLead.isPending ? 'Vytvářím...' : 'Vytvořit lead'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
